import { useEffect } from 'react';
import { Flex } from 'antd';
import StatusCardNotification from '@/components/StatusCardNotification';
import { ArrowLeft, ArrowRight } from '@/assets/icons/icons';
import './Style.css';
import { ShareAlertProps } from './types';
import { getAlertColors, getAlertIcon } from '../components/ShipmentAlerts';
import { trackEvent } from '@/hooks';
import { BeaconEventType } from '@/common/enum';

export default function ShareAlertsCarousel({
  alerts,
  index,
  setIndex,
  isSharePage,
  shipmentId,
}: ShareAlertProps) {
  const total = alerts?.length || 0;

  // Track the active alert becoming visible, same as the shipment detail
  // drawer's ShipmentAlerts. Only on the authenticated external-view route
  // — the public share link has no logged-in user, and the beacon endpoint
  // requires one.
  const activeAlertId = alerts?.[index]?.id;
  useEffect(() => {
    if (isSharePage || !activeAlertId || !shipmentId) return;
    trackEvent(BeaconEventType.AlertViewed, {
      reference_id: activeAlertId,
      shipment_id: shipmentId,
    });
  }, [isSharePage, activeAlertId, shipmentId]);

  if (!total) return null;

  const atFirst = index === 0;
  const atLast = index === total - 1;
  const current = alerts[index];
  const isBoundCall =
    current?.alert_type === 'in_bound' || current?.alert_type === 'out_bound';
  const is_gxp = current?.alert_type === 'temperature_gxp';
  const colors = getAlertColors(current, isBoundCall, is_gxp);
  return (
    <div className='sac'>
      {total > 1 && (
        <Flex vertical gap={8} style={{ marginBottom: '10px' }}>
          <Flex justify='space-between' align='center' className='sac__header'>
            <p className='sac__counter'>
              {index + 1} of {total} issues
            </p>

            <Flex gap={8} align='center' className='sac__actions'>
              <button
                className={`sac__nav ${atFirst ? 'is-disabled' : ''}`}
                title='Previous Alert'
                aria-label='Previous Alert'
                onClick={() => !atFirst && setIndex(index - 1)}
                disabled={atFirst}
              >
                <ArrowLeft />
              </button>

              <button
                className={`sac__nav ${atLast ? 'is-disabled' : ''}`}
                title='Next Alert'
                aria-label='Next Alert'
                onClick={() => !atLast && setIndex(index + 1)}
                disabled={atLast}
              >
                <ArrowRight />
              </button>
            </Flex>
          </Flex>
        </Flex>
      )}

      <StatusCardNotification
        timezone={current?.timezone}
        sharePage={true}
        cardStyle='style-1'
        icon={getAlertIcon(current, isBoundCall, is_gxp)}
        title={current?.title || ''}
        is_TP_exception={current?.is_transport_provider_exception}
        recommendedAction={current?.recommended_action}
        isCritical={current?.is_critical}
        description={current?.detail || ''}
        createdAt={current?.created_at || ''}
        bgColor={colors.bgColor}
        color={colors.color}
        showButtons={false}
        actionButtonText={
          is_gxp
            ? current?.status == 'awaiting_data'
              ? 'Investigate'
              : 'Review'
            : 'Mark as Resolved'
        }
        actionButtonBorderRadius='8px'
        actionButtonBgColor={colors.actionButtonBgColor}
      />
    </div>
  );
}
